import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { InquiryCTA } from "./PlanEventModal";

const attachSources = (v) => {
  if (v.dataset.loaded) return;
  v.dataset.loaded = "1";
  [["/hype-video.mp4", "video/mp4"], ["/hype-video.webm", "video/webm"]].forEach(([src, type]) => { const el = document.createElement("source"); el.src = src; el.type = type; v.appendChild(el); });
  v.load();
};
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const PARTICLES = [
  { l: "8%", t: "18%", s: 10, c: "#FFC20E", d: "14s", r: "12deg" }, { l: "16%", t: "70%", s: 7, c: "#149B9E", d: "17s", r: "-20deg" },
  { l: "27%", t: "30%", s: 6, c: "#fff", d: "19s", r: "35deg" }, { l: "72%", t: "22%", s: 8, c: "#149B9E", d: "15s", r: "-8deg" },
  { l: "84%", t: "64%", s: 11, c: "#FFC20E", d: "18s", r: "25deg" }, { l: "92%", t: "34%", s: 6, c: "#fff", d: "16s", r: "-30deg" },
  { l: "50%", t: "88%", s: 7, c: "#FFC20E", d: "20s", r: "10deg" }, { l: "40%", t: "12%", s: 5, c: "#149B9E", d: "13s", r: "40deg" },
];

export const VideoSection = () => {
  const videoRef = useRef(null);
  const frameRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [manual, setManual] = useState(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  // Prerendered HTML may carry a different state; the "mounted" token forces React to rewrite className after hydration.
  const showPlay = manual && !playing;
  const vis = (on) => `${mounted ? "mounted" : ""} ${on ? "flex" : "hidden"}`;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true; v.defaultMuted = true;
    if (reducedMotion()) { setManual(true); return; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        attachSources(v);
        v.play().then(() => { setPlaying(true); setManual(false); }).catch((err) => { if (err?.name === "NotAllowedError") setManual(true); });
      } else if (!v.paused) v.pause();
    }, { rootMargin: "200px 0px", threshold: 0.25 });
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const onMove = (e) => {
    const f = frameRef.current;
    if (!f || window.matchMedia("(hover: none)").matches || reducedMotion()) return;
    const r = f.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
    f.style.transform = `perspective(1400px) rotateX(${(-y * 6).toFixed(2)}deg) rotateY(${(x * 6).toFixed(2)}deg)`;
  };
  const onLeave = () => { if (frameRef.current) frameRef.current.style.transform = ""; };
  const manualPlay = () => { const v = videoRef.current; attachSources(v); setManual(false); v.play().then(() => setPlaying(true)).catch(() => {}); };

  return (
    <section data-testid="home-video" className="relative isolate overflow-hidden bg-[#0B0B0B] text-white">
      <div aria-hidden data-testid="video-edge-top" className="absolute inset-x-0 top-0 z-10 h-1 bg-en-yellow" />
      <div aria-hidden data-testid="video-edge-bottom" className="absolute inset-x-0 bottom-0 z-10 h-1 bg-en-yellow" />
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[90%] w-[min(1300px,110vw)] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-70" style={{ background: "radial-gradient(ellipse at center, rgba(20,155,158,0.28) 0%, rgba(255,194,14,0.10) 35%, rgba(11,11,11,0) 70%)" }} />
      {PARTICLES.map((p, i) => <span key={i} aria-hidden className="video-particle" style={{ left: p.l, top: p.t, width: p.s, height: p.s, background: p.c, "--d": p.d, "--r": p.r }} />)}

      <div className="relative mx-auto max-w-6xl px-5 pb-28 pt-32 text-center sm:px-8 lg:pb-36 lg:pt-40">
        <p className="eyebrow !text-en-yellow">See the fun in action</p>
        <h2 className="font-display mt-4 text-3xl text-white sm:text-4xl lg:text-5xl">This is what a great event feels like.</h2>
        <p className="mx-auto mt-4 max-w-xl text-base text-white/70">A quick look at the energy our hosts bring into the room.</p>

        <div className="video-stage mx-auto mt-12 w-full max-w-[1100px]" onMouseMove={onMove} onMouseLeave={onLeave}>
          <div ref={frameRef} className="video-frame relative transition-transform duration-300 ease-out">
            <div aria-hidden className="video-glow" />
            <div aria-hidden className="video-ring" />
            <div className="relative overflow-hidden rounded-[18px] bg-black ring-1 ring-white/70 sm:rounded-[28px]">
              <div className="relative aspect-video w-full">
                <video ref={videoRef} data-testid="hype-video" className="absolute inset-0 h-full w-full object-cover" poster="/images/w/event-3-800.webp" muted loop playsInline preload="none" onPlaying={() => { setPlaying(true); setManual(false); }} onPause={() => setPlaying(false)} />
                <span data-testid="video-live-tag" className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 font-gothic text-[11px] uppercase tracking-wider text-white backdrop-blur-sm sm:left-6 sm:top-6">
                  <span className={`h-2 w-2 rounded-full bg-en-yellow ${playing ? "animate-pulse" : ""}`} /> {playing ? "Live" : "Watch the fun"}
                </span>
                <button type="button" data-testid="video-play-btn" onClick={manualPlay} aria-label="Play video" className={`group absolute inset-0 items-center justify-center bg-black/30 transition-colors hover:bg-black/40 ${vis(showPlay)}`}>
                    <span className="flex h-20 w-20 items-center justify-center rounded-full bg-en-yellow text-black shadow-xl transition-transform duration-300 group-hover:scale-110"><Play className="ml-1 h-8 w-8" fill="currentColor" /></span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
          <InquiryCTA label="Book Here" icon data-testid="video-plan-event-btn" className="btn-primary !px-8 !py-4" />
          <Link to="/experiences" data-testid="video-explore-btn" data-track="explore_experiences" className="btn-outline btn-outline-light !px-8 !py-4">Explore Experiences</Link>
        </div>
      </div>
    </section>
  );
};
